import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * AuthCallback — processes the Emergent-managed Google OAuth redirect.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Emergent auth redirects the user to /auth-callback#session_id=<uuid>
 * after completing Google's OAuth dance. This component:
 *   1. Grabs the session_id synchronously from the URL fragment
 *   2. POSTs it to our backend /api/auth/google-session (which server-side
 *      exchanges it for the real user identity via Emergent, then upserts
 *      the user in db.users and mints an app JWT)
 *   3. Stores the JWT in localStorage and populates AuthContext
 *   4. Redirects to /discover
 *
 * Guarded by a useRef so the effect fires exactly once even under React
 * StrictMode's dev-mode double-invoke.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("Signing you in…");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Parse session_id from URL fragment (not query params)
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setStatus("No session found — returning to sign in.");
      setTimeout(() => nav("/login", { replace: true }), 1500);
      return;
    }

    (async () => {
      try {
        const r = await api.post("/auth/google-session", { session_id: sessionId });
        localStorage.setItem("spark_token", r.data.token);
        setUser(r.data.user);
        // Clear the session_id from the URL so it can't be replayed if the
        // user bookmarks the page.
        window.history.replaceState({}, "", "/discover");
        nav("/discover", { replace: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Google sign-in failed", err?.response?.data || err);
        setStatus("Google sign-in failed — returning to sign in.");
        setTimeout(() => nav("/login", { replace: true }), 2000);
      }
    })();
  }, [nav, setUser]);

  return (
    <div className="min-h-[100dvh] bg-[#16171A] text-[#F0E6D2] flex flex-col items-center justify-center px-6 text-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#C9A961] mb-6" />
      <p className="font-display text-2xl tracking-tight" data-testid="auth-callback-status">
        {status}
      </p>
    </div>
  );
}
